import { useEffect, useRef, useState } from "react";
import "emoji-picker-element";

declare global {
  namespace JSX {
    interface IntrinsicElements {
      "emoji-picker": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & { locale?: string; class?: string },
        HTMLElement
      >;
    }
  }
}

interface EmojiPickerPopoverProps {
  open: boolean;
  onClose: () => void;
  onPick: (unicode: string) => void;
  anchorRef: React.RefObject<HTMLElement | null>;
}

export function EmojiPickerPopover({ open, onClose, onPick, anchorRef }: EmojiPickerPopoverProps) {
  const pickerRef = useRef<HTMLElement | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (!open) return;
    setIsMobile(window.matchMedia("(max-width: 640px)").matches);
  }, [open]);

  useEffect(() => {
    if (!open || isMobile) {
      setPos(null);
      return;
    }
    const anchor = anchorRef.current;
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const PICKER_W = 340;
    const PICKER_H = 420;
    let left = r.left;
    let top = r.bottom + 6;
    if (left + PICKER_W > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - PICKER_W - 8);
    }
    if (top + PICKER_H > window.innerHeight - 8) {
      top = Math.max(8, r.top - PICKER_H - 6);
    }
    setPos({ top, left });
  }, [open, isMobile, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const el = pickerRef.current;
    if (!el) return;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ unicode: string }>).detail;
      if (detail?.unicode) onPick(detail.unicode);
    };
    el.addEventListener("emoji-click", handler);
    return () => el.removeEventListener("emoji-click", handler);
  }, [open, onPick]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (shellRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  if (isMobile) {
    return (
      <div className="emoji-picker-backdrop" onMouseDown={onClose}>
        <div
          ref={shellRef}
          className="emoji-picker-shell emoji-picker-shell--sheet"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <emoji-picker
            ref={pickerRef as React.RefObject<HTMLElement>}
            locale="ru"
            class="emoji-picker-el"
          />
        </div>
      </div>
    );
  }

  if (!pos) return null;
  return (
    <div
      ref={shellRef}
      className="emoji-picker-shell"
      style={{ top: pos.top, left: pos.left }}
    >
      <emoji-picker
        ref={pickerRef as React.RefObject<HTMLElement>}
        locale="ru"
        class="emoji-picker-el"
      />
    </div>
  );
}
